import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import {
  useAdminGetSystemNotice,
  useAdminUpdateSystemNotice,
  useAdminDeleteSystemNotice,
  getAdminListSystemNoticesQueryKey,
  getAdminGetSystemNoticeQueryKey,
} from '@/api/auth/admin/admin';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { FroalaEditor } from '@/components/ui/froala-editor';
import { Trash2, Check, Circle, Save } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/services/logger';
import type { SystemNoticeContentResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { useLanguages, getLanguageName, getSupportedLanguageCodes } from '@/hooks/useLanguages';

/**
 * Admin System Notice Detail page for editing notice settings and content
 *
 * Features:
 * - Settings tab: product, severity, status, acknowledgement mode, dates
 * - Content tabs: per language (en, fr, de, etc.)
 * - Delete notice (draft only)
 * - Save changes with API integration
 */
export default function AdminSystemNoticeDetailPage() {
  const { noticeId } = useParams<{ noticeId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { languages } = useLanguages();

  // Fetch notice data
  const {
    data: noticeData,
    isLoading,
    error,
  } = useAdminGetSystemNotice(noticeId!, {
    query: { enabled: !!noticeId },
  });

  const notice = noticeData?.data;

  // Local state for settings
  const [productId, setProductId] = useState(notice?.productId || '');
  const [severity, setSeverity] = useState(notice?.severity || 'Info');
  const [acknowledgementMode, setAcknowledgementMode] = useState(
    notice?.acknowledgementMode || 'None'
  );
  const [startsAt, setStartsAt] = useState(notice?.startsAt || '');
  const [endsAt, setEndsAt] = useState(notice?.endsAt || '');

  // Local state for content (keyed by language)
  const [contentByLang, setContentByLang] = useState<Record<string, SystemNoticeContentResponse>>(
    {}
  );

  // Track original values for change detection
  const [originalSettings, setOriginalSettings] = useState({
    productId: '',
    severity: '',
    acknowledgementMode: '',
    startsAt: '',
    endsAt: '',
  });
  const [originalContentByLang, setOriginalContentByLang] = useState<
    Record<string, SystemNoticeContentResponse>
  >({});

  // Track save status per section
  const [lastSavedSection, setLastSavedSection] = useState<string | null>(null);

  // Active language for content editing
  const [activeLanguage, setActiveLanguage] = useState<string>('en');

  // Initialize local state when data loads
  useEffect(() => {
    if (notice) {
      const settings = {
        productId: notice.productId,
        severity: notice.severity,
        acknowledgementMode: notice.acknowledgementMode,
        startsAt: notice.startsAt,
        endsAt: notice.endsAt || '',
      };

      setProductId(settings.productId);
      setSeverity(settings.severity);
      setAcknowledgementMode(settings.acknowledgementMode);
      setStartsAt(settings.startsAt);
      setEndsAt(settings.endsAt);
      setOriginalSettings(settings);

      const contentMap: Record<string, SystemNoticeContentResponse> = {};
      notice.contents.forEach((content) => {
        contentMap[content.language] = content;
      });
      setContentByLang(contentMap);
      setOriginalContentByLang(JSON.parse(JSON.stringify(contentMap)));
    }
  }, [notice]);

  // Clear saved indicator after 3 seconds
  useEffect(() => {
    if (lastSavedSection) {
      const timer = setTimeout(() => setLastSavedSection(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [lastSavedSection]);

  // Update mutation
  const { mutate: updateNotice, isPending: isUpdating } = useAdminUpdateSystemNotice({
    mutation: {
      onSuccess: () => {
        toast.success('System notice updated successfully');
        queryClient.invalidateQueries({
          queryKey: getAdminListSystemNoticesQueryKey(),
        });
      },
      onError: (error: Error) => {
        logger.error(error, {
          context: 'UpdateSystemNotice',
          noticeId,
        });
        toast.error('Failed to update system notice. ' + (error.message || 'Unknown error'));
      },
    },
  });

  // Delete mutation
  const { mutate: deleteNotice, isPending: isDeleting } = useAdminDeleteSystemNotice({
    mutation: {
      onSuccess: () => {
        toast.success('System notice deleted successfully');
        queryClient.invalidateQueries({
          queryKey: getAdminListSystemNoticesQueryKey(),
        });
        navigate('/dashboard/admin/system-notices');
      },
      onError: (error: Error) => {
        logger.error(error, {
          context: 'DeleteSystemNotice',
          noticeId,
        });
        toast.error('Failed to delete system notice. ' + (error.message || 'Unknown error'));
      },
    },
  });

  /**
   * Save settings
   */
  const handleSaveSettings = () => {
    if (!noticeId || !notice) return;

    // Convert contentByLang to array, using current edited values
    const contentsArray = Object.values(contentByLang);

    updateNotice(
      {
        noticeId,
        data: {
          productId: productId.trim(),
          severity,
          status: notice.status, // Keep current status
          acknowledgementMode,
          startsAt,
          endsAt: endsAt || undefined,
          contents: contentsArray, // Use current edited content
        },
      },
      {
        onSuccess: () => {
          setOriginalSettings({
            productId,
            severity,
            acknowledgementMode,
            startsAt,
            endsAt,
          });
          setLastSavedSection('settings');
        },
      }
    );
  };

  /**
   * Save content for a specific language
   */
  const handleSaveContent = (language: string) => {
    if (!noticeId || !notice) return;

    const updatedContent = contentByLang[language];
    if (!updatedContent) return;

    // Sanitize HTML content for the language being saved
    const sanitizedContentByLang = {
      ...contentByLang,
      [language]: {
        ...updatedContent,
        bodyHtml: DOMPurify.sanitize(updatedContent.bodyHtml),
      },
    };

    // Convert contentByLang to array, using current edited values for ALL languages
    const contentsArray = Object.values(sanitizedContentByLang);

    updateNotice(
      {
        noticeId,
        data: {
          productId,
          severity,
          status: notice.status,
          acknowledgementMode,
          startsAt,
          endsAt: endsAt || undefined,
          contents: contentsArray,
        },
      },
      {
        onSuccess: () => {
          setOriginalContentByLang((prev) => ({
            ...prev,
            [language]: JSON.parse(JSON.stringify(contentByLang[language])),
          }));
          setLastSavedSection(`content-${language}`);
        },
      }
    );
  };

  /**
   * Update content field for a language
   */
  const updateContentField = (
    language: string,
    field: keyof SystemNoticeContentResponse,
    value: string
  ) => {
    setContentByLang((prev) => ({
      ...prev,
      [language]: {
        ...prev[language],
        [field]: value,
      },
    }));
  };

  /**
   * Handle delete notice
   */
  const handleDelete = () => {
    if (!noticeId) return;
    deleteNotice({ noticeId });
  };

  /**
   * Check if settings have unsaved changes
   */
  const hasUnsavedSettingsChanges = () => {
    return (
      productId !== originalSettings.productId ||
      severity !== originalSettings.severity ||
      acknowledgementMode !== originalSettings.acknowledgementMode ||
      startsAt !== originalSettings.startsAt ||
      endsAt !== originalSettings.endsAt
    );
  };

  /**
   * Check if content has unsaved changes for a language
   */
  const hasUnsavedContentChanges = (language: string) => {
    const current = contentByLang[language];
    const original = originalContentByLang[language];

    if (!current || !original) return false;

    return (
      current.title !== original.title ||
      current.summary !== original.summary ||
      current.bodyHtml !== original.bodyHtml
    );
  };

  /**
   * Check if all required translations exist
   */
  const hasAllRequiredTranslations = () => {
    if (!notice) return false;
    const requiredLanguages = getSupportedLanguageCodes(languages);
    return requiredLanguages.every(lang =>
      notice.contents.some(c => c.language === lang)
    );
  };

  /**
   * Get display status with badge variant
   */
  const getDisplayStatus = () => {
    if (!notice) return { label: 'Unknown', variant: 'secondary' as const };

    // Show "Scheduled" for published notices with future start date
    if (notice.status === 'Published' && new Date(notice.startsAt) > new Date()) {
      return { label: 'Scheduled', variant: 'outline' as const };
    }

    switch (notice.status) {
      case 'Draft':
        return { label: 'Draft', variant: 'secondary' as const };
      case 'Published':
        return { label: 'Published', variant: 'default' as const };
      case 'Archived':
        return { label: 'Archived', variant: 'destructive' as const };
      default:
        return { label: notice.status, variant: 'default' as const };
    }
  };

  /**
   * Publish notice (requires all translations)
   */
  const handlePublish = () => {
    if (!noticeId || !notice) return;

    // Convert contentByLang to array, using current edited values
    const contentsArray = Object.values(contentByLang);

    updateNotice(
      {
        noticeId,
        data: {
          productId,
          severity,
          status: 'Published',
          acknowledgementMode,
          startsAt,
          endsAt: endsAt || undefined,
          contents: contentsArray,
        },
      },
      {
        onSuccess: () => {
          const isScheduled = new Date(startsAt) > new Date();
          toast.success(
            isScheduled
              ? 'System notice scheduled successfully'
              : 'System notice published successfully'
          );
          // Refresh the notice data to update UI
          queryClient.invalidateQueries({
            queryKey: getAdminGetSystemNoticeQueryKey(noticeId),
          });
        },
      }
    );
  };

  /**
   * Archive notice
   */
  const handleArchive = () => {
    if (!noticeId || !notice) return;

    // Convert contentByLang to array, using current edited values
    const contentsArray = Object.values(contentByLang);

    updateNotice(
      {
        noticeId,
        data: {
          productId,
          severity,
          status: 'Archived',
          acknowledgementMode,
          startsAt,
          endsAt: endsAt || undefined,
          contents: contentsArray,
        },
      },
      {
        onSuccess: () => {
          toast.success('System notice archived successfully');
          // Refresh the notice data to update UI
          queryClient.invalidateQueries({
            queryKey: getAdminGetSystemNoticeQueryKey(noticeId),
          });
        },
      }
    );
  };

  /**
   * Add a new translation
   */
  const handleAddTranslation = (languageCode: string) => {
    if (!noticeId || !notice) return;

    // Check if language already exists
    if (contentByLang[languageCode]) {
      toast.error(
        `Translation for "${getLanguageName(languages, languageCode)}" already exists`
      );
      return;
    }

    // Create new content entry
    const newContent: SystemNoticeContentResponse = {
      language: languageCode,
      title: '',
      summary: '',
      bodyHtml: '',
      lastEditedBy: '',
      lastEditedAt: new Date().toISOString(),
    };

    // Add to contentByLang and convert to array
    const updatedContentByLang = {
      ...contentByLang,
      [languageCode]: newContent,
    };
    const contentsArray = Object.values(updatedContentByLang);

    updateNotice(
      {
        noticeId,
        data: {
          productId,
          severity,
          status: notice.status,
          acknowledgementMode,
          startsAt,
          endsAt: endsAt || undefined,
          contents: contentsArray,
        },
      },
      {
        onSuccess: () => {
          toast.success(
            `Translation for "${getLanguageName(languages, languageCode)}" added successfully`
          );
          // Refresh the notice data
          queryClient.invalidateQueries({
            queryKey: getAdminGetSystemNoticeQueryKey(noticeId),
          });
          // Switch to the new language
          setActiveLanguage(languageCode);
        },
      }
    );
  };

  /**
   * Format datetime-local input value
   */
  const formatDateTimeLocal = (isoString: string) => {
    if (!isoString) return '';
    return new Date(isoString).toISOString().slice(0, 16);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-[200px]" />
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  if (error || !notice) {
    return (
      <div className="text-center text-destructive py-8">
        <p>Failed to load system notice</p>
        <Button onClick={() => navigate('/dashboard/admin/system-notices')} className="mt-4">
          Back to List
        </Button>
      </div>
    );
  }

  const noticeLanguages = notice.contents.map((c) => c.language);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Edit System Notice</h1>
          <p className="text-sm text-muted-foreground">ID: {notice.id}</p>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant={getDisplayStatus().variant}>{getDisplayStatus().label}</Badge>
          {notice.status === 'Draft' && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={isDeleting}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete System Notice?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete the system notice.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle>Notice Settings</CardTitle>
          <CardDescription>Configure the basic settings for this notice</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Product ID */}
          <div className="space-y-2">
            <Label htmlFor="product-id">Product ID</Label>
            <Input
              id="product-id"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              disabled={isUpdating}
            />
          </div>

          {/* Severity */}
          <div className="space-y-2">
            <Label htmlFor="severity">Severity</Label>
            <Select value={severity} onValueChange={setSeverity} disabled={isUpdating}>
              <SelectTrigger id="severity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Info">Info</SelectItem>
                <SelectItem value="Urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Acknowledgement Mode */}
          <div className="space-y-2">
            <Label htmlFor="ack-mode">Acknowledgement Mode</Label>
            <Select
              value={acknowledgementMode}
              onValueChange={setAcknowledgementMode}
              disabled={isUpdating}
            >
              <SelectTrigger id="ack-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="User">User</SelectItem>
                <SelectItem value="Org">Organization</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Start Date */}
          <div className="space-y-2">
            <Label htmlFor="starts-at">Start Date & Time</Label>
            <Input
              id="starts-at"
              type="datetime-local"
              value={formatDateTimeLocal(startsAt)}
              onChange={(e) => setStartsAt(new Date(e.target.value).toISOString())}
              disabled={isUpdating}
            />
          </div>

          {/* End Date */}
          <div className="space-y-2">
            <Label htmlFor="ends-at">End Date & Time (Optional)</Label>
            <Input
              id="ends-at"
              type="datetime-local"
              value={formatDateTimeLocal(endsAt)}
              onChange={(e) =>
                setEndsAt(e.target.value ? new Date(e.target.value).toISOString() : '')
              }
              disabled={isUpdating}
            />
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button
              onClick={handleSaveSettings}
              disabled={isUpdating || !hasUnsavedSettingsChanges()}
            >
              <Save className="h-4 w-4 mr-2" />
              {isUpdating ? 'Saving...' : 'Save Settings'}
              {lastSavedSection === 'settings' && <Check className="h-4 w-4 ml-2 text-green-500" />}
            </Button>

            {/* Publish button - only show for Draft notices */}
            {notice.status === 'Draft' && (
              <Button
                onClick={handlePublish}
                disabled={!hasAllRequiredTranslations() || isUpdating}
                variant="default"
              >
                Publish
              </Button>
            )}

            {/* Archive button - only show for Published notices */}
            {notice.status === 'Published' && (
              <Button onClick={handleArchive} disabled={isUpdating} variant="destructive">
                Archive
              </Button>
            )}
          </div>

          {/* Show warning if translations are missing */}
          {notice.status === 'Draft' && !hasAllRequiredTranslations() && (
            <p className="text-sm text-muted-foreground">
              All translations ({languages.map((l) => l.name).join(', ')}) are required to publish
            </p>
          )}
        </CardContent>
      </Card>

      {/* Content Card */}
      <Card>
        <CardHeader>
          <CardTitle>Notice Content</CardTitle>
          <CardDescription>Edit the notice content for each language</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Language Selector */}
          <div className="grid gap-2">
            <Label htmlFor="language-select">Language</Label>
            <Select value={activeLanguage} onValueChange={setActiveLanguage}>
              <SelectTrigger id="language-select" className="w-[240px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => {
                  const hasContent = noticeLanguages.includes(lang.code);
                  const isDirty = hasUnsavedContentChanges(lang.code);
                  return (
                    <SelectItem key={lang.code} value={lang.code}>
                      <span className="flex items-center gap-2">
                        {lang.name}
                        {hasContent && !isDirty && <Check className="h-3 w-3 text-green-600" />}
                        {isDirty && <Circle className="h-3 w-3 fill-yellow-500 text-yellow-500" />}
                        {!hasContent && (
                          <span className="text-xs text-muted-foreground">(not added)</span>
                        )}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Content Form or Add Translation */}
          {noticeLanguages.includes(activeLanguage) ? (
            <div className="space-y-4">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={contentByLang[activeLanguage]?.title || ''}
                  onChange={(e) => updateContentField(activeLanguage, 'title', e.target.value)}
                  disabled={isUpdating}
                />
              </div>

              {/* Summary */}
              <div className="space-y-2">
                <Label htmlFor="summary">Summary</Label>
                <Textarea
                  id="summary"
                  value={contentByLang[activeLanguage]?.summary || ''}
                  onChange={(e) => updateContentField(activeLanguage, 'summary', e.target.value)}
                  disabled={isUpdating}
                  rows={3}
                />
              </div>

              {/* Body HTML */}
              <div className="space-y-2">
                <Label htmlFor="body">Body</Label>
                <FroalaEditor
                  value={contentByLang[activeLanguage]?.bodyHtml || ''}
                  onChange={(content) => updateContentField(activeLanguage, 'bodyHtml', content)}
                  disabled={isUpdating}
                />
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button
                  onClick={() => handleSaveContent(activeLanguage)}
                  disabled={isUpdating || !hasUnsavedContentChanges(activeLanguage)}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isUpdating ? 'Saving...' : 'Save Content'}
                  {lastSavedSection === `content-${activeLanguage}` && (
                    <Check className="h-4 w-4 ml-2 text-green-500" />
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 border rounded-md bg-muted/50">
              <p className="text-muted-foreground mb-4">
                No content for {getLanguageName(languages, activeLanguage)} yet.
              </p>
              <Button onClick={() => handleAddTranslation(activeLanguage)} disabled={isUpdating}>
                Add {getLanguageName(languages, activeLanguage)} Translation
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
